import KatalLogger from '@amzn/katal-logger';
import { isNil, mapKeys, omit, snakeCase, merge } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import objectSize from './helpers/ObjectSize';
import { katalDomain, appName } from '../MetricsPublisher/DomainUtils';
import KatalLoggerConfig from './KatalLoggerConfig';
import { PULSE_EXCEPTION_NAMES } from './constants';
/**
 * Katal uses `sendBeacon()` for transferring data. While implementation is
 * browser-specific, most support a payload of 64KB max (content plus headers).
 * @see https://nicj.net/beaconing-in-practice/#beaconing-payload-limits
 */
const BEACON_LOG_CHAR_SIZE = 65536; // 64KB
const MAX_LOG_UTILIZATION_FACTOR = 0.9; // 90% of total beacon size for request body.
/**
 * Maximum allowed log size in Bytes. Used to set the `maxLogLineSize` config
 * attribute.
 * @see https://katal.amazon.dev/for-developers/metrics/logger/
 */
const MAX_LOG_LINE_SIZE = Math.floor(BEACON_LOG_CHAR_SIZE * MAX_LOG_UTILIZATION_FACTOR);
const DEFAULT_CONTEXT = {
    // AppName to differentiate Katal logs from different applications
    appName,
    // Create UUID to track logs from current page load and join against.
    pageLoadId: uuidv4(),
};
const DEFAULT_CONTEXT_SIZE = objectSize(DEFAULT_CONTEXT);
const LOG_BUFFER = 1024; // 1KB
const MAX_CONTENT_SIZE = MAX_LOG_LINE_SIZE - DEFAULT_CONTEXT_SIZE - LOG_BUFFER;
class KatalLoggerUtilities {
    constructor() {
        /**
         * Wrapper function for KatalLogger which validates the log size and decides which
         * log type to use based on the arguments passed.
         * @param {string} message - the human-readable string identifying the log line.
         * @param {Record<string, unknown>} context - additional data JSON object to log.
         * @param {Error} errorToLog - an optional error object to log.
         */
        this.log = (message, context, errorToLog) => {
            try {
                this.validateLogSize(message, context);
                if (typeof errorToLog !== 'undefined') {
                    this.logger.fatal(message, errorToLog, context);
                }
                else {
                    this.logger.info(message, context);
                }
            }
            catch (error) {
                // Log the exception without the full message and context to reduce the size.
                this.logger.fatal(error.message, error);
            }
        };
        /**
         * Formats the context JSON required for logging page_view logs and calls the log wrapper function.
         * @see {@link log|log wrapper function}
         */
        this.logPageView = () => {
            const context = this.getContext();
            this.log('PAGE_VIEW', context);
        };
        /**
         * Custom logger for error handling which is a callback function for KatalLogger errorListener which calls
         * the log wrapper function.
         * @param {Error | PulseException} error - error object to log.
         * @param {Record<string, unknown>} context - Additional data JSON object to log.
         * @see {@link log|log wrapper function}
         */
        this.logError = (error, context = {}) => {
            const defaultContext = this.getContext();
            const mergedContext = merge(context, defaultContext);
            if (PULSE_EXCEPTION_NAMES.includes(error.name)) {
                this.log(error.name, mergedContext, error);
            }
            this.log(error.message, mergedContext, error);
        };
        /**
         * Function to prepare a data JSON object which includes location, hostType and user attributes details
         * which are required for the Page View logs.
         * @return {Record<string, unknown>} context - Additional details JSON object.
         */
        this.getContext = () => {
            const { location } = window;
            const urlParams = new URLSearchParams(window.location.search);
            const params = mapKeys(urlParams, (value, key) => snakeCase(key));
            const context = {
                params,
                hostType: this.stage,
                userAttributes: {},
                location: {
                    ...omit(location, ['href', 'search', 'port']),
                    url: location.href,
                },
            };
            if (isNil(this.userAttributes)) {
                this.setUserAttributes();
            }
            context.userAttributes = this.userAttributes;
            return context;
        };
        /**
         * Sets the KatalLoggerUtilities object's userAttributes with the current user's Federate claims.
         */
        this.setUserAttributes = () => {
            this.userAttributes = JSON.parse(window.CLAIMS || '{}');
        };
        /**
         * Determines whether the total size of the log request body can be processed
         *  by KatalLogger given the size limits imposed by its underlying dependency
         *  on `sendBeacon()`.
         * @param message - Human readable message included in the log.
         * @param context - Object that provides additional details necessary to interpret the log.
         * @throws RangeError - When message and context size exceed allowed limit.
         */
        // eslint-disable-next-line class-methods-use-this
        this.validateLogSize = (message, context) => {
            const contentSize = objectSize(message) + objectSize(context);
            if (MAX_CONTENT_SIZE >= contentSize)
                return;
            throw new RangeError(`Could not log request. Content size (${contentSize} Bytes) exceeds available size (${MAX_CONTENT_SIZE} Bytes). Reduce the size by truncating the message or limiting values passed in the context parameters.`);
        };
        this.stage = katalDomain;
        this.logger = new KatalLogger({
            context: DEFAULT_CONTEXT,
            logToConsole: this.stage === 'dev',
            maxLogsPerBatch: 1,
            maxLogLineSize: MAX_LOG_LINE_SIZE,
            url: KatalLoggerConfig.get(katalDomain).ENDPOINT, // If an url is not provided, logs are written to console.
        });
        this.setUserAttributes();
        // this.logger.addMetricsListener();
        this.logger.addPerformanceListener();
        this.logger.addErrorListener((error) => {
            // Add this check because error is an optional parameter to the filterCallback function
            // and in the logError we are trying to access message property of the error provided.
            if (error) {
                this.logError(error, { uncaughtException: true });
            }
            // Circumvent the error listener's publisher since we handle that
            // ourselves with `logError`, and returning true double logs the error.
            return false;
        });
    }
}
export default KatalLoggerUtilities;
//# sourceMappingURL=KatalLoggerUtilities.js.map